import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, RotateCcw } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

const QUESTIONS = [
  { q: "Do your jobs hand you design models — DWG, DXF or LandXML?", yes: "d3" },
  { q: "Does the machine need to know where it sits on big sites — long pads, no clear view of a laser?", yes: "d25" },
  { q: "Are you holding levels, falls or batters against a laser or benchmark?", yes: "d2" },
];

const RESULTS = {
  d3: {
    title: "Full 3D — MCNAV TX73",
    body: "Your sites hand you models. Auto-follow surfaces, live cut/fill and as-built sign-off — the full drawing in the cab.",
    to: "/products/3d",
    cta: "View the 3D system",
  },
  d25: {
    title: "2.5D — EasyNAV EMG100",
    body: "The machine knows where it sits and you set the grades in the field. ±30 mm, installed in under 2 hours.",
    to: "/products/2.5d",
    cta: "View the 2.5D system",
  },
  d2: {
    title: "2D tier — MCNAV TX73",
    body: "Depth, slope and fall against a benchmark — and the same hardware steps up to full 3D any time.",
    to: "/hire",
    cta: "View the 2D tier",
  },
  talk: {
    title: "Let's talk it through",
    body: "Your work doesn't fit the usual boxes — a quick call will land the right system first go.",
    to: "/contact",
    cta: "Talk to a specialist",
  },
};

const BTN =
  "inline-flex flex-1 items-center justify-center gap-2 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide transition-colors";

export default function CompareChooser({ onResult }) {
  const [step, setStep] = useState(0);
  const [path, setPath] = useState([]);
  const done = step >= QUESTIONS.length;
  const resultKey = done ? [...path].reverse().find((p) => p !== "no") || "talk" : null;
  const result = done ? RESULTS[resultKey] : null;

  const answer = (yes) => {
    const key = yes ? QUESTIONS[step].yes : "no";
    const nextPath = [...path, key];
    setPath(nextPath);
    if (yes || step + 1 >= QUESTIONS.length) {
      setStep(QUESTIONS.length);
      const rk = [...nextPath].reverse().find((p) => p !== "no") || "talk";
      onResult?.(rk);
    } else {
      setStep(step + 1);
    }
  };

  const reset = () => {
    setStep(0);
    setPath([]);
    onResult?.(null);
  };

  return (
    <div className="border border-line bg-deep/70 p-8 sm:p-10" data-testid="compare-chooser">
      {!done ? (
        <>
          <div className="flex items-center justify-between">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">
              Question {step + 1} of {QUESTIONS.length}
            </p>
            <div className="flex gap-1.5">
              {QUESTIONS.map((_, i) => (
                <span key={i} className={`h-1.5 w-6 ${i <= step ? "bg-signal" : "bg-line"}`} />
              ))}
            </div>
          </div>
          <h3 className="mt-5 max-w-2xl font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
            {QUESTIONS[step].q}
          </h3>
          <div className="mt-8 flex gap-4">
            <button
              type="button"
              data-testid="chooser-yes"
              onClick={() => answer(true)}
              className={`${BTN} bg-brand text-cream hover:bg-signal hover:text-deep`}
            >
              Yes
              <ArrowUpRight className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="chooser-no"
              onClick={() => answer(false)}
              className={`${BTN} border border-signal/50 text-neon hover:bg-signal/10`}
            >
              No
            </button>
          </div>
          {step > 0 && (
            <button
              type="button"
              data-testid="chooser-back"
              onClick={() => { setStep(step - 1); setPath(path.slice(0, -1)); onResult?.(null); }}
              className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:text-neon"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          )}
        </>
      ) : (
        <FadeUp>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">Your match</p>
          <h3 className="mt-4 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
            {result.title}
          </h3>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-fog">{result.body}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to={result.to}
              data-testid="chooser-result-cta"
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-white"
            >
              {result.cta}
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <button
              type="button"
              data-testid="chooser-reset"
              onClick={reset}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:text-neon"
            >
              <RotateCcw className="h-4 w-4" /> Start over
            </button>
          </div>
        </FadeUp>
      )}
    </div>
  );
}
